/**
 * [OffersV3]
 *
 * This file contains the old mapping logic between the new Offers Model V3 and
 * the old daedalus backend augmentation format.
 *
 * Added as part of [this epic](https://app.shortcut.com/findhotel/epic/62938/offers-v3-model-search-clients-migration)
 * as a temporary solution while we migrate all usages to the new models.
 * Should be deleted after the epic is completed.
 */

import differenceInDays from 'date-fns/differenceInDays'
import parseISO from 'date-fns/parseISO'
import {mapObjIndexed} from 'ramda'

import {calculateTotalRate} from '@daedalus/core/src/offer/business/calculateTotalRate'
import {
  Offer,
  OfferPrice,
  OfferPromoKey
} from '@daedalus/core/src/offer/types/offer'
import {RaaOfferWithPrices} from '@daedalus/core/src/offer/types/Raa'
import {SearchOffer} from '@daedalus/core/src/offer/types/SearchOffer'
import {sortOffersByPrice} from '@daedalus/core/src/offer/utils/sortOffers'
import {numberOfRooms} from '@daedalus/core/src/room/business/roomConfiguration'
import {
  HotelOfferEntity as SapiHotelOfferEntity,
  Offer as SapiOffer,
  SplitBookingEntity
} from '@findhotel/sapi'

import {increasePositionIndexByOne} from '../../analytics/utils/trackEventHelpers'
import {hasAnchorPrice} from './price'

const PRICE_DIFFERENCE_THRESHOLD = 5
const DEFAULT_MIN_DIFFERENCE_PERCENTAGE = 0.02

declare const isUtcDate: unique symbol
type UtcDate = Date & {
  [isUtcDate]: true
}

interface RateBreakdown {
  taxes: number
  baseRate: number
  localTaxes: number
}

export interface AnchorPriceRateBreakdown extends RateBreakdown {
  calculatedTotalRate: number
  totalRate: number
  nightlyRate: number
}

interface TopOfferData {
  anchorPrice?: number
  anchorPriceNightly?: number
  offerIndexes: number[]
}

export type SapiOfferWithChargeablePrices = SearchOffer & {
  prices: OfferPrice[]
}

export const getIsSapiOffer = (
  offer: RaaOfferWithPrices | SapiOfferWithChargeablePrices | Offer
): offer is SapiOfferWithChargeablePrices => {
  return Boolean((offer as SapiOfferWithChargeablePrices)?.package)
}

export type OldHotelOfferEntity = Pick<
  SapiHotelOfferEntity,
  'availableOffersCount' | 'id' | 'discount' | 'splitBooking'
> & {
  anchorPriceRateBreakdown?: AnchorPriceRateBreakdown
  cheapestPriceRateBreakdown?: RateBreakdown
  fetchedAllOffers: boolean
  hasMoreOffers: boolean
  topOfferData?: TopOfferData
  offers: SearchOffer[]
  roomsConfig?: string
  promos?: Record<string, SearchOffer>
}

export interface MappingContext {
  checkIn: string
  checkOut: string
  numberOfRooms: number
  includeTaxes?: boolean
  includeLocalTaxes?: boolean
  includeRoomsInNightlyPrice?: boolean
}

function dateToMiddayUTC(date: string): UtcDate {
  return parseISO(`${date} 12:00:00`) as UtcDate
}

export function calculateNightlyRate(
  totalRate: number,
  checkIn: string,
  checkOut: string
) {
  const nights = differenceInDays(
    dateToMiddayUTC(checkOut),
    dateToMiddayUTC(checkIn)
  )

  if (nights < 1) {
    throw new Error('checkOut cannot be the same or less than checkIn')
  }
  return nights === 1 ? totalRate : totalRate / nights
}

function sapiOfferToOffer({
  offer,
  context,
  anchorPrice,
  index
}: {
  offer: SapiOffer & {roomsConfig?: string} & {isSplitBookingEligible?: boolean}
  context: MappingContext
  anchorPrice: number
  index?: number
}): SearchOffer {
  const calculatedTotalRate = calculateTotalRate(
    offer.rate,
    Boolean(context.includeTaxes),
    Boolean(context.includeLocalTaxes)
  )

  const calculatedTotalRateWithTax = calculateTotalRate(offer.rate, true, true)

  const {roomsConfig} = offer

  const numberOfRoomsToInclude =
    roomsConfig && context.includeRoomsInNightlyPrice
      ? numberOfRooms(roomsConfig)
      : context.numberOfRooms

  const nightlyRate = calculateNightlyRate(
    context.includeRoomsInNightlyPrice
      ? calculatedTotalRate * numberOfRoomsToInclude
      : calculatedTotalRate,
    context.checkIn,
    context.checkOut
  )

  const nightlyRateWithTax = calculateNightlyRate(
    context.includeRoomsInNightlyPrice
      ? calculatedTotalRateWithTax * numberOfRoomsToInclude
      : calculatedTotalRateWithTax,
    context.checkIn,
    context.checkOut
  )

  const tags = offer.tags ?? []
  const isTopOffer = tags.includes('top_offer')

  const osoOfferPosition = increasePositionIndexByOne(index) ?? undefined

  return {
    ...offer,
    calculatedTotalRate,
    hasAnchorPrice: isTopOffer
      ? hasAnchorPrice(calculatedTotalRate, anchorPrice)
      : false,
    nightlyRate,
    totalRate: calculatedTotalRateWithTax * numberOfRoomsToInclude,
    tags,
    nightlyRateWithTax,
    osoOfferPosition
  }
}

export const calculateNightlyRateWithRoomsLogic = (
  totalRate: number,
  numberOfRooms: number,
  checkIn: string,
  checkOut: string,
  includeRoomsInNightlyPrice: boolean
) =>
  calculateNightlyRate(
    includeRoomsInNightlyPrice ? totalRate * numberOfRooms : totalRate,
    checkIn,
    checkOut
  )

// Creates split booking from a SapiHotelOfferEntity.
export const makeSplitBooking = (
  offerEntity: SapiHotelOfferEntity,
  context: MappingContext
): SplitBookingEntity | undefined => {
  if (!offerEntity?.splitBooking) {
    return undefined
  }

  const splitBooking = {
    ...offerEntity.splitBooking,
    offers:
      offerEntity.splitBooking.offers?.map(splitBookingOffer => {
        const contextPerOffer = {
          ...context,
          checkIn: splitBookingOffer.checkIn,
          checkOut: splitBookingOffer.checkOut
        }
        const splitBookingTotalRate = calculateTotalRate(
          splitBookingOffer.offer.rate,
          Boolean(contextPerOffer.includeTaxes),
          Boolean(contextPerOffer.includeLocalTaxes)
        )
        return {
          ...splitBookingOffer,
          offer: sapiOfferToOffer({
            offer: splitBookingOffer.offer,
            context: contextPerOffer,
            anchorPrice: splitBookingTotalRate
          })
        }
      }) ?? []
  }

  return splitBooking
}

/**
 * @ToDo[Search] Should be removed after the feature toggle '07f4b5ed-split-booking-fake-door' is no longer needed
 * Determines whether a hotel offer has fake splitBooking
 * @param {SapiOffer} cheapestOffer - The cheapest offer.
 * @param {SapiOffer} secondCheapestOffer - The second cheapest offer.
 * @returns boolean - Returns true if the offer has in range
 */
export const getIsSplitBookingEligible = (
  cheapestOffer: SapiOffer,
  secondCheapestOffer: SapiOffer
): boolean => {
  if (!cheapestOffer || !secondCheapestOffer) return false

  const cheapestOfferPrice = calculateTotalRate(cheapestOffer.rate, true, true)
  const secondCheapestOfferPrice = calculateTotalRate(
    secondCheapestOffer.rate,
    true,
    true
  )

  return getIsSplitBookingEligibleByPrice(
    cheapestOfferPrice,
    secondCheapestOfferPrice
  )
}

/**
 * @ToDo[Search] Should be removed after the feature toggle '07f4b5ed-split-booking-fake-door' is no longer needed
 * Determines whether a hotel offer has fake splitBooking
 * @param cheapestOfferPrice - The cheapest offer price.
 * @param secondCheapestOfferPrice - The second cheapest offer price.
 * @param minDiffPercentage - The minimum difference percentage.
 * @returns boolean - Returns true if the offer has in range
 */
export const getIsSplitBookingEligibleByPrice = (
  cheapestOfferPrice: number,
  secondCheapestOfferPrice: number,
  minDiffPercentage = DEFAULT_MIN_DIFFERENCE_PERCENTAGE
): boolean => {
  // Calculate the price difference
  const difference = secondCheapestOfferPrice - cheapestOfferPrice

  // Determine the minimum required difference
  const minRequiredDifference = Math.max(
    PRICE_DIFFERENCE_THRESHOLD,
    minDiffPercentage * secondCheapestOfferPrice
  )

  return difference >= minRequiredDifference
}

export const calculateAnchorPriceRateBreakdown = (
  offerEntity: SapiHotelOfferEntity,
  context: MappingContext,
  mostExpensiveOffer: SapiOffer
): AnchorPriceRateBreakdown => {
  const {roomsConfig} = offerEntity

  const numberOfRoomsToInclude =
    roomsConfig && context.includeRoomsInNightlyPrice
      ? numberOfRooms(roomsConfig)
      : context.numberOfRooms

  const anchorPriceRateBreakdown = {
    base: mostExpensiveOffer?.rate?.base,
    hotelFees: mostExpensiveOffer?.rate?.hotelFees,
    taxes: mostExpensiveOffer?.rate?.taxes
  }

  const calculatedTotalRate = calculateTotalRate(
    anchorPriceRateBreakdown,
    Boolean(context.includeTaxes),
    Boolean(context.includeLocalTaxes)
  )

  const totalRate =
    anchorPriceRateBreakdown.base +
    anchorPriceRateBreakdown.taxes +
    anchorPriceRateBreakdown.hotelFees

  const nightlyRate = calculateNightlyRateWithRoomsLogic(
    calculatedTotalRate,
    numberOfRoomsToInclude,
    context.checkIn,
    context.checkOut,
    Boolean(context.includeRoomsInNightlyPrice)
  )

  return {
    taxes: anchorPriceRateBreakdown.taxes,
    baseRate: anchorPriceRateBreakdown.base,
    localTaxes: anchorPriceRateBreakdown.hotelFees,
    calculatedTotalRate,
    totalRate,
    nightlyRate
  }
}

export function sapiHotelOfferEntityToHotelOfferEntity(
  offerEntity: SapiHotelOfferEntity,
  context: MappingContext
): OldHotelOfferEntity | undefined {
  if (!offerEntity) return undefined

  const sortedOffers = sortOffersByPrice(offerEntity.offers)
  const mostExpensiveOffer = sortedOffers[sortedOffers.length - 1]

  const anchorPriceRateBreakdown = calculateAnchorPriceRateBreakdown(
    offerEntity,
    context,
    mostExpensiveOffer
  )

  const {roomsConfig} = offerEntity

  const fetchedAllOffers =
    offerEntity.offers.length >= offerEntity.availableOffersCount
  const hasMoreOffers = !fetchedAllOffers

  const offerIsEligible = getIsSplitBookingEligible(
    sortedOffers[0],
    sortedOffers[1]
  )

  const eligibleOfferId = offerIsEligible ? sortedOffers[0].id : null

  const daedalusModelOffers = offerEntity.offers?.map((offer, index) => {
    // Determine if the offer should be replaced by the web2app promo offer
    const chosenOffer =
      offerEntity.promos?.[OfferPromoKey.Web2App]?.id === offer.id
        ? offerEntity.promos[OfferPromoKey.Web2App]
        : offer

    return sapiOfferToOffer({
      offer: {
        ...chosenOffer,
        isSplitBookingEligible: offer.id === eligibleOfferId,
        roomsConfig
      },
      context,
      anchorPrice: anchorPriceRateBreakdown.calculatedTotalRate,
      index
    })
  })

  return {
    anchorPriceRateBreakdown,
    availableOffersCount: offerEntity.availableOffersCount,
    fetchedAllOffers,
    hasMoreOffers,
    id: offerEntity.id,
    offers: daedalusModelOffers,
    topOfferData: {
      anchorPrice: anchorPriceRateBreakdown.calculatedTotalRate,
      anchorPriceNightly: anchorPriceRateBreakdown.nightlyRate,
      offerIndexes: [0, 1, 2, 3]
    },
    roomsConfig: offerEntity.roomsConfig,
    discount: offerEntity.discount,
    splitBooking: makeSplitBooking(offerEntity, context),
    promos: offerEntity.promos
      ? mapObjIndexed(
          offer =>
            sapiOfferToOffer({
              offer,
              context,
              anchorPrice: anchorPriceRateBreakdown.calculatedTotalRate
            }),
          offerEntity.promos || {}
        )
      : undefined
  }
}
